import { Component, signal, inject, OnInit } from '@angular/core';
import { RouterOutlet, Router, NavigationError } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NgxSonnerToaster } from 'ngx-sonner';
import { toast } from './shared/utils/toast';
import { Store } from '@ngrx/store';
import { SupabaseService } from './core/services/supabase.service';
import { SecurityService } from './core/services/security.service';
import { selectSession, selectUserInfo } from './core/store/auth/auth.selectors';
import * as AuthActions from './core/store/auth/auth.actions';
import { filter, take } from 'rxjs/operators';
import { Capacitor } from '@capacitor/core';
import { GlobalDialogManagerService } from './core/services/global-dialog-manager.service';
import { ClinicSwitchService } from './core/services/clinic-switch.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, NgxSonnerToaster],
  templateUrl: './app.html',
  styleUrl: './app.css'
})
export class App implements OnInit {
  // Guards against a reload loop if a chunk is genuinely unreachable (e.g. offline)
  // rather than just stale from a deploy.
  private static readonly CHUNK_RELOAD_KEY = 'app-chunk-reload-ts';
  private static readonly CHUNK_RELOAD_COOLDOWN_MS = 60_000;
  private static readonly CHUNK_LOAD_ERROR_PATTERN =
    /failed to fetch dynamically imported module|chunkloaderror|loading chunk .* failed|failed to load module script/i;

  protected readonly title = signal('metasmile');
  private translate = inject(TranslateService);
  private store = inject(Store);
  private supabase = inject(SupabaseService);
  private securityService = inject(SecurityService);
  private router = inject(Router);
  private globalDialogManager = inject(GlobalDialogManagerService);
  private clinicSwitch = inject(ClinicSwitchService);

  constructor() {
    this.translate.addLangs(['en', 'pt', 'es', 'pt-BR']);
    // Fallback locale for any key missing in the active language. Kept as 'en'
    // because it's the most complete locale, so gaps show readable English rather
    // than raw keys. The default language the user actually sees is 'pt' (below).
    this.translate.setDefaultLang('en');

    const savedLang = localStorage.getItem('language');
    if (savedLang) {
      this.translate.use(savedLang);
    } else {
      this.translate.use('pt');
    }

    // Initialize global modal tracker
    this.globalDialogManager.init();

    // Switch clinic: follow a switch made in another tab, and after a switch say where we are.
    // get(), not instant(): this runs before the locale file has loaded.
    this.clinicSwitch.listen();
    const switchedTo = this.clinicSwitch.takeSwitchedNotice();
    if (switchedTo) {
      this.translate
        .get('clinic_switch.done', { clinic: switchedTo })
        .pipe(take(1))
        .subscribe((text: string) => toast.success(text));
    }

    // A stale tab can still reference lazy-chunk hashes from before the latest
    // deploy; the router surfaces that as a NavigationError rather than throwing.
    this.router.events
      .pipe(filter((event): event is NavigationError => event instanceof NavigationError))
      .subscribe(event => this.handleNavigationError(event));
  }

  private handleNavigationError(event: NavigationError): void {
    const message = String((event.error as Error)?.message ?? event.error ?? '');
    if (!App.CHUNK_LOAD_ERROR_PATTERN.test(message)) {
      // Anything that isn't a stale chunk used to be dropped on the floor here,
      // so a failing guard cancelled the navigation with no page, no log and no
      // message. Surface it instead. No redirect: guards own their own fallback,
      // and redirecting on every NavigationError risks a loop.
      console.error(`Navigation to "${event.url}" failed.`, event.error);
      toast.error(this.translate.instant('navigation_failed'));
      return;
    }

    const lastReload = Number(sessionStorage.getItem(App.CHUNK_RELOAD_KEY) ?? 0);
    const now = Date.now();
    if (now - lastReload < App.CHUNK_RELOAD_COOLDOWN_MS) {
      console.error('Chunk load failed again shortly after a reload; not retrying to avoid a reload loop.', event.error);
      return;
    }

    sessionStorage.setItem(App.CHUNK_RELOAD_KEY, String(now));
    // Full navigation (not just reload) so the user lands where they were headed.
    window.location.href = event.url || window.location.href;
  }

  async ngOnInit() {

    // Listen for auth state changes during app runtime
    try {
      this.supabase.client.auth.onAuthStateChange((event, session) => {
        try {
          if (event === 'PASSWORD_RECOVERY') {
            if (session) {
              this.store.dispatch(AuthActions.sessionLoaded({ session }));
            }
            if (!window.location.pathname.includes('/reset-password')) {
              this.router.navigate(['/reset-password']);
            }
          } else if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session) {
            this.store.select(selectSession).pipe(take(1)).subscribe(currentSession => {
              if (!currentSession || currentSession.user.id !== session.user.id) {
                this.store.dispatch(AuthActions.sessionLoaded({ session }));
              }
            });
          } else if (event === 'USER_UPDATED' && session) {
            // updateUser() has already persisted this session; mirror it into the
            // store so the must_change_password gate sees the new user_metadata.
            // Covers every password-change screen at once, and arrives over the
            // BroadcastChannel in other tabs too.
            //
            // Deliberately NOT sessionLoaded: that drives rehydrateSession$ and
            // would refetch the whole profile on each password change.
            this.store.dispatch(AuthActions.sessionUpdated({ session }));
          } else if (event === 'SIGNED_OUT') {
            console.warn('Supabase Auth Event: SIGNED_OUT detected');
            this.globalDialogManager.dismissAll();
            this.store.select(selectSession).pipe(take(1)).subscribe(currentSession => {
              if (currentSession) {
                console.log('Session was active in store, triggering app sign out');
                this.store.dispatch(AuthActions.signOut());
                toast.info('Session expired. Please log in again.', { id: 'auto-logout' });
                // Without this the user is left sitting on a guarded route they
                // can no longer load — the reason a failed rehydration used to
                // dead-end on a blank screen.
                this.router.navigate(['/login']);
              } else {
                console.log('No active session in store, ignoring SIGNED_OUT event');
                this.router.navigate(['/login']);
              }
            });
          }
        } catch (err) {
          console.error('Auth state change handler error:', err);
        }
      });
    } catch (e) {
      console.error('Auth listener setup error:', e);
    }
  }
}
